import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

// Schéma de validation pour le formulaire utilisateur
const userSchema = yup.object({
  firstName: yup.string().required('Le prénom est requis'),
  lastName: yup.string().required('Le nom est requis'),
  email: yup.string().required('L\'email est requis').email('Format d\'email invalide'),
  phone: yup.string().nullable(),
  
  // Champs spécifiques MSD Portail
  isCompany: yup.boolean(),
  companyName: yup.string().when('isCompany', {
    is: true,
    then: () => yup.string().required('Le nom de l\'entreprise est requis'),
    otherwise: () => yup.string()
  }),
  siretNumber: yup.string().when('isCompany', {
    is: true,
    then: () => yup.string().required('Le numéro SIRET est requis'),
    otherwise: () => yup.string()
  }),
  vatNumber: yup.string(),
  
  // Adresse
  address1: yup.string(),
  city: yup.string(),
  postalCode: yup.string(),
  country: yup.string(),
  
  // Abonnement
  subscriptionPlan: yup.string(),
  subscriptionStatus: yup.string().oneOf(['active', 'inactive', 'pending', 'expired'], 'Statut d\'abonnement invalide'),
}).required();

// Conversion robuste d'une date Firestore / Date JS / string ISO → date locale courte
const toLocalDate = (value) => {
  if (!value) return 'N/A';
  try {
    const date = typeof value.toDate === 'function'
      ? value.toDate()
      : value.seconds
        ? new Date(value.seconds * 1000)
        : value instanceof Date
          ? value
          : new Date(value);
    return date.toLocaleDateString('fr-FR');
  } catch (error) {
    return 'N/A';
  }
};

const UserModal = ({ isOpen, onClose, onSave, user }) => {
  const isEditMode = !!user;
  const [activeTab, setActiveTab] = useState('personal');
  const [isSaving, setIsSaving] = useState(false);
  
  // Liste des onglets : « Compte » (fiche lecture) en premier, puis édition
  const tabs = [
    { id: 'account', label: 'Compte' },
    { id: 'personal', label: 'Informations' },
    { id: 'company', label: 'Entreprise' },
    { id: 'address', label: 'Adresse' },
    { id: 'subscription', label: 'Abonnement' },
    { id: 'identifiers', label: 'Identifiants' }
  ];
  
  // Initialiser react-hook-form avec le schéma de validation
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    resolver: yupResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      
      isCompany: user?.isCompany || false,
      companyName: user?.companyName || '',
      siretNumber: user?.siretNumber || '',
      vatNumber: user?.vatNumber || '',
      
      address1: user?.address1 || '',
      city: user?.city || '',
      postalCode: user?.postalCode || '',
      state: user?.state || '',
      country: user?.country || '',
      
      subscriptionPlan: user?.subscriptionPlan || 'free',
      subscriptionStatus: user?.subscriptionStatus || 'inactive',
      
      msdId: user?.msdId || '',
      username: user?.username || '',
      stripeCustomerId: user?.stripeCustomerId || '',
      subscriptionId: user?.subscriptionId || '',
      welcomeEmailSent: user?.welcomeEmailSent || false,
    }
  });
  
  const isCompany = watch('isCompany');
  
  // Réinitialiser le formulaire lorsque l'utilisateur change
  useEffect(() => {
    if (isOpen) {
      // Toujours ouvrir sur l'onglet « Compte » en édition (fiche), « Informations » en création
      setActiveTab(isEditMode ? 'account' : 'personal');
      reset({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        
        isCompany: user?.isCompany || false,
        companyName: user?.companyName || '',
        siretNumber: user?.siretNumber || '',
        vatNumber: user?.vatNumber || '',
        
        address1: user?.address1 || '',
        city: user?.city || '',
        postalCode: user?.postalCode || '',
        state: user?.state || '',
        country: user?.country || '',
        
        subscriptionPlan: user?.subscriptionPlan || 'free',
        subscriptionStatus: user?.subscriptionStatus || 'inactive',
        
        msdId: user?.msdId || '',
        username: user?.username || '',
        stripeCustomerId: user?.stripeCustomerId || '',
        subscriptionId: user?.subscriptionId || '',
        welcomeEmailSent: user?.welcomeEmailSent || false,
      });
    }
  }, [user, isOpen, reset, isEditMode]);

  // Gérer la soumission du formulaire
  const onSubmit = async (data) => {
    setIsSaving(true);
    try {
      await onSave(data);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50" 
        onClick={onClose}
        aria-hidden="true"
      ></div>
      
      {/* Modal */}
      <div 
        className="bg-base-100 rounded-lg shadow-xl max-w-3xl w-full mx-4 z-10 max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* En-tête de la modale */}
        <div className="px-5 py-4 border-b border-base-200 flex justify-between items-center sticky top-0 bg-base-100 z-10">
          <h3 id="modal-title" className="text-lg font-bold text-base-content">
            {isEditMode ? 'Modifier l\'utilisateur' : 'Ajouter un utilisateur'}
          </h3>
          <button 
            onClick={onClose} 
            className="text-base-content/70 hover:text-base-content transition-colors rounded-full p-1"
            aria-label="Fermer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex flex-col h-full">
          {/* Tabs - Version simple */}
          <div className="border-b border-base-200">
            <div className="flex overflow-x-auto px-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 whitespace-nowrap ${
                    activeTab === tab.id 
                      ? 'border-b-2 border-primary text-primary font-medium' 
                      : 'text-base-content/70 hover:text-base-content'
                  }`}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  id={`tab-${tab.id}`}
                  aria-controls={`panel-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Contenu du formulaire */}
          <div className="p-5 overflow-y-auto">
            <form id="user-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Compte (fiche lecture) */}
              <div
                className={activeTab === 'account' ? 'block' : 'hidden'}
                role="tabpanel"
                id="panel-account"
                aria-labelledby="tab-account"
              >
                <h4 className="font-medium text-base-content mb-4">Compte</h4>
                <div className="bg-base-200/30 border border-base-200 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-base-content/60 uppercase tracking-wide">Type de compte</p>
                      <span className={`inline-block mt-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                        user?.isCompany
                          ? 'bg-info/20 text-info border border-info/30'
                          : 'bg-success/20 text-success border border-success/30'
                      }`}>
                        {user?.isCompany ? 'Entreprise' : 'Particulier'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-base-content/60 uppercase tracking-wide">Date d'inscription</p>
                      <p className="text-base-content font-medium mt-1.5">{toLocalDate(user?.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-base-content/60 uppercase tracking-wide">Abonnement</p>
                      <p className="text-base-content font-medium mt-1.5">
                        {(user?.subscriptionPlan && user.subscriptionPlan !== 'free' ? user.subscriptionPlan : 'Free')}
                        {user?.subscriptionStatus === 'active' ? ' · Actif' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-base-200">
                    <p className="text-xs text-base-content/60 uppercase tracking-wide">Identifiant du compte (uid)</p>
                    <p className="text-xs font-mono text-base-content/70 mt-1 break-all">{user?.id || '—'}</p>
                  </div>
                </div>
              </div>

              {/* Informations personnelles */}
              <div 
                className={activeTab === 'personal' ? 'block' : 'hidden'}
                role="tabpanel"
                id="panel-personal"
                aria-labelledby="tab-personal"
              >
                <h4 className="font-medium text-base-content mb-4">Informations personnelles</h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label htmlFor="firstName" className="label">
                      <span className="label-text text-base-content">Prénom *</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      placeholder="Prénom"
                      {...register('firstName')}
                      className={`input input-bordered w-full ${errors.firstName ? 'input-error' : ''}`}
                    />
                    {errors.firstName && <span className="text-xs text-error mt-1">{errors.firstName.message}</span>}
                  </div>
                  
                  <div className="form-control">
                    <label htmlFor="lastName" className="label">
                      <span className="label-text text-base-content">Nom *</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      placeholder="Nom"
                      {...register('lastName')}
                      className={`input input-bordered w-full ${errors.lastName ? 'input-error' : ''}`}
                    />
                    {errors.lastName && <span className="text-xs text-error mt-1">{errors.lastName.message}</span>}
                  </div>
                </div>
                
                <div className="form-control mt-2">
                  <label htmlFor="email" className="label">
                    <span className="label-text text-base-content">Email *</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="exemple@domaine.com"
                    {...register('email')}
                    className={`input input-bordered w-full ${errors.email ? 'input-error' : ''}`}
                  />
                  {errors.email && <span className="text-xs text-error mt-1">{errors.email.message}</span>}
                </div>
                
                <div className="form-control mt-2">
                  <label htmlFor="phone" className="label">
                    <span className="label-text text-base-content">Téléphone</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    placeholder="+33 6 12 34 56 78"
                    {...register('phone')}
                    className="input input-bordered w-full"
                  />
                </div>

                <div className="form-control mt-4">
                  <label className="cursor-pointer label justify-start gap-2">
                    <input
                      type="checkbox"
                      {...register('isCompany')}
                      className="checkbox checkbox-primary"
                    />
                    <span className="label-text text-base-content">C'est une entreprise</span>
                  </label>
                  {isCompany && (
                    <div className="mt-2 p-2 bg-base-200/50 rounded-md text-sm text-base-content/70">
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-info" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                        </svg>
                        <span>Veuillez compléter les informations d'entreprise dans l'onglet Entreprise.</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Entreprise */}
              <div 
                className={activeTab === 'company' ? 'block' : 'hidden'}
                role="tabpanel"
                id="panel-company"
                aria-labelledby="tab-company"
              >
                <h4 className="font-medium text-base-content mb-4">Informations d'entreprise</h4>
                
                <div className="form-control">
                  <label className="cursor-pointer label justify-start gap-2">
                    <input
                      type="checkbox"
                      {...register('isCompany')}
                      className="checkbox checkbox-primary"
                    />
                    <span className="label-text text-base-content">C'est une entreprise</span>
                  </label>
                </div>

                {isCompany ? (
                  <div className="space-y-4 mt-2">
                    <div className="form-control">
                      <label htmlFor="companyName" className="label">
                        <span className="label-text text-base-content">Nom de l'entreprise *</span>
                      </label>
                      <input
                        id="companyName"
                        type="text"
                        placeholder="Nom de l'entreprise"
                        {...register('companyName')}
                        className={`input input-bordered w-full ${errors.companyName ? 'input-error' : ''}`}
                      />
                      {errors.companyName && <span className="text-xs text-error mt-1">{errors.companyName.message}</span>}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-control">
                        <label htmlFor="siretNumber" className="label">
                          <span className="label-text text-base-content">Numéro SIRET *</span>
                        </label>
                        <input
                          id="siretNumber"
                          type="text"
                          placeholder="12345678900012"
                          {...register('siretNumber')}
                          className={`input input-bordered w-full ${errors.siretNumber ? 'input-error' : ''}`}
                        />
                        {errors.siretNumber && <span className="text-xs text-error mt-1">{errors.siretNumber.message}</span>}
                      </div>
                      
                      <div className="form-control">
                        <label htmlFor="vatNumber" className="label">
                          <span className="label-text text-base-content">Numéro TVA</span>
                        </label>
                        <input
                          id="vatNumber"
                          type="text"
                          placeholder="FR12345678900"
                          {...register('vatNumber')}
                          className="input input-bordered w-full"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-base-200/50 p-4 rounded-lg text-center mt-4">
                    <p className="text-base-content/70">Activez l'option "C'est une entreprise" pour remplir les informations d'entreprise.</p>
                  </div>
                )}
              </div>
              
              {/* Adresse */}
              <div 
                className={activeTab === 'address' ? 'block' : 'hidden'}
                role="tabpanel"
                id="panel-address"
                aria-labelledby="tab-address"
              >
                <h4 className="font-medium text-base-content mb-4">Adresse</h4>
                
                <div className="form-control">
                  <label htmlFor="address1" className="label">
                    <span className="label-text text-base-content">Adresse</span>
                  </label>
                  <input
                    id="address1"
                    type="text"
                    placeholder="123 rue de l'Exemple"
                    {...register('address1')}
                    className="input input-bordered w-full"
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                  <div className="form-control">
                    <label htmlFor="city" className="label">
                      <span className="label-text text-base-content">Ville</span>
                    </label>
                    <input
                      id="city"
                      type="text"
                      placeholder="Paris"
                      {...register('city')}
                      className="input input-bordered w-full"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label htmlFor="postalCode" className="label">
                      <span className="label-text text-base-content">Code postal</span>
                    </label>
                    <input
                      id="postalCode"
                      type="text"
                      placeholder="75000"
                      {...register('postalCode')}
                      className="input input-bordered w-full"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                  <div className="form-control">
                    <label htmlFor="state" className="label">
                      <span className="label-text text-base-content">État/Région</span>
                    </label>
                    <input
                      id="state"
                      type="text"
                      placeholder="Île-de-France"
                      {...register('state')}
                      className="input input-bordered w-full"
                    />
                  </div>
                  
                  <div className="form-control">
                    <label htmlFor="country" className="label">
                      <span className="label-text text-base-content">Pays</span>
                    </label>
                    <input
                      id="country"
                      type="text"
                      placeholder="France"
                      {...register('country')}
                      className="input input-bordered w-full"
                    />
                  </div>
                </div>
              </div>
              
              {/* Abonnement */}
              <div 
                className={activeTab === 'subscription' ? 'block' : 'hidden'}
                role="tabpanel"
                id="panel-subscription"
                aria-labelledby="tab-subscription"
              >
                <h4 className="font-medium text-base-content mb-4">Informations d'abonnement</h4>
                
                <div className="form-control">
                  <label htmlFor="subscriptionPlan" className="label">
                    <span className="label-text text-base-content">Forfait d'abonnement</span>
                  </label>
                  <select
                    id="subscriptionPlan"
                    {...register('subscriptionPlan')}
                    className="select select-bordered w-full"
                  >
                    <option value="free">Gratuit</option>
                    <option value="basic">Basique</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                
                <div className="form-control mt-2">
                  <label htmlFor="subscriptionStatus" className="label">
                    <span className="label-text text-base-content">Statut d'abonnement</span>
                  </label>
                  <select
                    id="subscriptionStatus"
                    {...register('subscriptionStatus')}
                    className="select select-bordered w-full"
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                    <option value="pending">En attente</option>
                    <option value="expired">Expiré</option>
                  </select>
                </div>
              </div>

              {/* Identifiants */}
              <div 
                className={activeTab === 'identifiers' ? 'block' : 'hidden'}
                role="tabpanel"
                id="panel-identifiers"
                aria-labelledby="tab-identifiers"
              >
                <h4 className="font-medium text-base-content mb-4">Identifiants système</h4>
                
                <div className="form-control">
                  <label htmlFor="msdId" className="label">
                    <span className="label-text text-base-content">MSD ID</span>
                  </label>
                  <input
                    id="msdId"
                    type="text"
                    placeholder="MSD-12345"
                    {...register('msdId')}
                    className="input input-bordered w-full"
                  />
                  <span className="text-xs text-base-content/60 mt-1">Identifiant unique sur la plateforme MSD.</span>
                </div>
                
                <div className="form-control mt-2">
                  <label htmlFor="username" className="label">
                    <span className="label-text text-base-content">Nom d'utilisateur</span>
                  </label>
                  <input
                    id="username"
                    type="text"
                    placeholder="utilisateur123"
                    {...register('username')}
                    className="input input-bordered w-full"
                  />
                </div>
                
                <div className="bg-base-200/50 p-4 rounded-lg mt-4">
                  <h5 className="font-medium text-base-content mb-3">Informations Stripe</h5>
                  
                  <div className="form-control">
                    <label htmlFor="stripeCustomerId" className="label">
                      <span className="label-text text-base-content">Stripe Customer ID</span>
                    </label>
                    <input
                      id="stripeCustomerId"
                      type="text"
                      placeholder="cus_xyz123abc"
                      {...register('stripeCustomerId')}
                      className="input input-bordered w-full opacity-75"
                      disabled={isEditMode}
                    />
                  </div>
                  
                  <div className="form-control mt-2">
                    <label htmlFor="subscriptionId" className="label">
                      <span className="label-text text-base-content">Subscription ID</span>
                    </label>
                    <input
                      id="subscriptionId"
                      type="text"
                      placeholder="sub_xyz123abc"
                      {...register('subscriptionId')}
                      className="input input-bordered w-full opacity-75"
                      disabled={isEditMode}
                    />
                  </div>
                </div>
                
                <div className="form-control mt-4">
                  <label className="cursor-pointer label justify-start gap-2">
                    <input
                      type="checkbox"
                      {...register('welcomeEmailSent')}
                      className="checkbox checkbox-primary"
                    />
                    <span className="label-text text-base-content">Email de bienvenue envoyé</span>
                  </label>
                </div>
              </div>
            </form>
          </div>
          
          {/* Footer simple */}
          <div className="px-5 py-4 border-t border-base-200 flex justify-end items-center sticky bottom-0 bg-base-100">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="user-form"
                className={`btn btn-primary ${isSaving ? 'loading' : ''}`}
                disabled={isSaving}
              >
                {isSaving ? 'Enregistrement...' : (isEditMode ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserModal;